import React, { useState, useRef, useCallback } from "react";
import { Modal } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";

const CLOCK_SIZE = 220;
const CLOCK_RADIUS = CLOCK_SIZE / 2;
const NUMBER_RADIUS = 85;
const HAND_LENGTH = NUMBER_RADIUS;

// Hour and minute values for the clock face
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

// Renders the interactive clock face for hour/minute selection
const ClockFace = ({ mode, selectedValue, onSelect, onMouseDone }) => {
  const clockRef = useRef(null);
  const dragging = useRef(false);

  // Calculate hour/minute value from mouse/touch event
  const getValueFromEvent = useCallback(
    (e) => {
      if (!clockRef.current) return null;
      const rect = clockRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const angle = (Math.atan2(clientX - cx, cy - clientY) * 180) / Math.PI;
      const normalized = (angle + 360) % 360;

      if (mode === "hour") {
        let hour = Math.round(normalized / 30);
        if (hour === 0) hour = 12;
        return hour;
      } else {
        let minute = Math.round(normalized / 6);
        if (minute === 60) minute = 0;
        return minute;
      }
    },
    [mode],
  );

  // Start dragging/selecting a value
  const handleStart = (e) => {
    e.preventDefault();
    dragging.current = true;
    const val = getValueFromEvent(e);
    if (val !== null) onSelect(val);
  };

  // Update value while dragging
  const handleMove = (e) => {
    if (!dragging.current) return;
    e.preventDefault();
    const val = getValueFromEvent(e);
    if (val !== null) onSelect(val);
  };

  // End dragging/selecting
  const handleEnd = () => {
    if (dragging.current) {
      dragging.current = false;
      if (onMouseDone) onMouseDone();
    }
  };

  // Calculate angle for clock hand
  const handAngle =
    mode === "hour" ? (selectedValue % 12) * 30 : selectedValue * 6;

  const numbers = mode === "hour" ? HOURS : MINUTES;

  return (
    <div
      ref={clockRef}
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      style={{
        width: CLOCK_SIZE,
        height: CLOCK_SIZE,
        borderRadius: "50%",
        background: "#ededed",
        position: "relative",
        cursor: "pointer",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "#02475c",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
        }}
      />

      <div
        style={{
          position: "absolute",
          bottom: "50%",
          left: "50%",
          width: 2,
          height: HAND_LENGTH,
          marginLeft: -1,
          background: "#02475c",
          transformOrigin: "center bottom",
          transform: `rotate(${handAngle}deg)`,
          zIndex: 1,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -18,
            left: -17,
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "#02475c",
          }}
        />
      </div>

      {numbers.map((num) => {
        const angleDeg = mode === "hour" ? (num % 12) * 30 - 90 : num * 6 - 90;
        const rad = (angleDeg * Math.PI) / 180;
        const x = CLOCK_RADIUS + NUMBER_RADIUS * Math.cos(rad);
        const y = CLOCK_RADIUS + NUMBER_RADIUS * Math.sin(rad);
        const isSelected = selectedValue === num;
        const label =
          mode === "minute" ? String(num).padStart(2, "0") : num;

        return (
          <div
            key={num}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: "translate(-50%, -50%)",
              borderRadius: "50%",
              fontSize: 14,
              fontWeight: isSelected ? 700 : 400,
              color: isSelected ? "#fff" : "#333",
              zIndex: 3,
              pointerEvents: "none",
            }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
};

// Main ClockPicker component: shows time, opens modal, handles selection
const ClockPicker = ({ value, onChange, placeholder, error }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("hour");
  const [hour, setHour] = useState(10);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState("AM");

  // Open the clock picker modal and parse initial value
  const openPicker = () => {
    if (value) {
      const trimmed = value.trim().toUpperCase();
      const hasAmPm = trimmed.endsWith("AM") || trimmed.endsWith("PM");
      if (hasAmPm) {
        const isPm = trimmed.endsWith("PM");
        const timePart = trimmed.replace(/(AM|PM)/i, "").trim();
        const parts = timePart.split(":");
        let h = parseInt(parts[0], 10) || 12;
        setMinute(parseInt(parts[1], 10) || 0);
        setPeriod(isPm ? "PM" : "AM");
        if (h === 0) h = 12;
        if (h > 12) { h = h - 12; setPeriod("PM"); }
        setHour(h);
      } else {
        const parts = value.split(":");
        let h24 = parseInt(parts[0], 10) || 0;
        setMinute(parseInt(parts[1], 10) || 0);
        setPeriod(h24 >= 12 ? "PM" : "AM");
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        setHour(h12);
      }
    } else {
      setHour(10);
      setMinute(0);
      setPeriod("AM");
    }
    setMode("hour");
    setOpen(true);
  };

  // Convert 12-hour time to 24-hour format
  const to24Hour = (h12, p) => {
    if (p === "AM") return h12 === 12 ? 0 : h12;
    return h12 === 12 ? 12 : h12 + 12;
  };

  // Confirm selection and send 24-hour value to parent
  const handleOk = () => {
    const h24 = to24Hour(hour, period);
    const hh = String(h24).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    onChange(`${hh}:${mm}`);
    setOpen(false);
  };

  // Format value for display in input (always 12-hour with AM/PM)
  const formatDisplay = () => {
    if (!value) return "";
    let h, m;
    if (typeof value === "string" && value.match(/^\d{1,2}:\d{2}$/)) {
      const parts = value.split(":");
      h = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
    } else if (typeof value === "string" && value.match(/\d{1,2}:\d{2}\s?(AM|PM)/i)) {
      const match = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      h = parseInt(match[1], 10);
      m = parseInt(match[2], 10);
      const ampm = match[3].toUpperCase();
      if (ampm === "PM" && h < 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
    } else {
      return value;
    }
    const suffix = h >= 12 ? "PM" : "AM";
    const displayH = h % 12 || 12;
    return `${displayH}:${String(m).padStart(2, "0")} ${suffix}`;
  };

  return (
    <>
      <div
        onClick={openPicker}
        style={{
          border: `1px solid ${error ? "#ff4d4f" : "#d9d9d9"}`,
          borderRadius: 4,
          padding: "4px 11px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          minHeight: 32,
          background: "#fff",
        }}
      >
        <span style={{ color: value ? "#333" : "#bfbfbf" }}>
          {value ? formatDisplay() : placeholder || "Select time"}
        </span>
        <ClockCircleOutlined style={{ color: "#bfbfbf" }} />
      </div>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleOk}
        okText="OK"
        width={320}
        centered
        bodyStyle={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px" }}
        closable={false}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              onClick={() => setMode("hour")}
              style={{
                fontSize: 40,
                fontWeight: 700,
                cursor: "pointer",
                color: mode === "hour" ? "#02475c" : "#999",
                transition: "color 0.2s",
              }}
            >
              {String(hour).padStart(2, "0")}
            </span>
            <span style={{ fontSize: 40, fontWeight: 700, color: "#999" }}>:</span>
            <span
              onClick={() => setMode("minute")}
              style={{
                fontSize: 40,
                fontWeight: 700,
                cursor: "pointer",
                color: mode === "minute" ? "#02475c" : "#999",
                transition: "color 0.2s",
              }}
            >
              {String(minute).padStart(2, "0")}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginLeft: 4 }}>
            <span
              onClick={() => setPeriod("AM")}
              style={{
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                padding: "2px 8px",
                borderRadius: 4,
                background: period === "AM" ? "#02475c" : "transparent",
                color: period === "AM" ? "#fff" : "#999",
                transition: "all 0.2s",
              }}
            >
              AM
            </span>
            <span
              onClick={() => setPeriod("PM")}
              style={{
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                padding: "2px 8px",
                borderRadius: 4,
                background: period === "PM" ? "#02475c" : "transparent",
                color: period === "PM" ? "#fff" : "#999",
                transition: "all 0.2s",
              }}
            >
              PM
            </span>
          </div>
        </div>

        <ClockFace
          mode={mode}
          selectedValue={mode === "hour" ? hour : minute}
          onSelect={(val) => {
            if (mode === "hour") setHour(val);
            else setMinute(val);
          }}
          onMouseDone={() => {
            if (mode === "hour") setMode("minute");
          }}
        />
      </Modal>
    </>
  );
};

export default ClockPicker;
